import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import withContext from '../ContextAPI/Context_HOC';
import { Row, Col } from 'react-flexbox-grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { CircularProgress } from '@material-ui/core';
import { combineLatest } from 'rxjs';
import useForm from '../Utilities/useForm';
import { byAgeDisplay } from '../Utilities/UtilityFunctions';
import ErrorMessageToUser from '../Error/ErrorMessageToUser';
import { Link } from 'react-router-dom';
import MembershipNote from './MembershipNote';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#54676B',
    color: theme.palette.common.white,
    textAlign: 'center',
    border: '1px solid black',
  },
  body: {
    fontSize: 14,
    textAlign: 'center',
    fontWeight: 'bold',
    border: '1px solid black',
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#FBF9F8',
    },
  },
}))(TableRow);

const useStyles = makeStyles(() => ({
  table: {
    border: '1px',
  },
}));

const validationSchema = {
  selfDesignatedHouseholdName: { required: false },
};

// eslint-disable-next-line no-unused-vars
const HouseholdInfo = (props) => {
  const myProfileProxy = props.context.myProfileProxy;
  const householdProxy = props.context.householdProxy;
  const setChildRegistrationSuccessMessage = props.context.setChildRegistrationSuccessMessage;
  const childRegistrationSuccessMessage = props.context.childRegistrationSuccessMessage;
  const setPageTitle = props.setPageTitle;
  useEffect(() => setPageTitle('Household Information'), [setPageTitle]);
  const classes = useStyles();
  const [defaultHouseholdName, setDefaultHouseholdName] = useState('');
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [childrenOfLoggedInUser, setChildrenOfLoggedInUser] = useState([]);
  const initialState = { selfDesignatedHouseholdName: { value: '' } };
  const [messageToUser, setMessageToUser] = useState(null);
  const [badErrorMessage, setBadErrorMessage] = useState(null);
  const [showCircularProgress, setShowCircularProgress] = useState(true);

  const saveHouseholdInfo = useCallback(
    (state) => {
      const profileData = {
        selfDesignatedHouseholdName: state.selfDesignatedHouseholdName.value,
      };
      setShowCircularProgress(true);
      setMessageToUser(null);

      const myProfileProxySetSubscription = myProfileProxy.set(profileData).subscribe({
        next: () => {
          setMessageToUser(
            <p className="success-message wider" data-cy="success-message">
              Successfully Updated!
            </p>
          );
          setShowCircularProgress(false);
        },
        error: (e) => {
          if (e.response?.status === 400 && e.response?.data) {
            const message = e.response.data.message ?? JSON.stringify(e.response?.data);
            setMessageToUser(
              <p className="error-message wider" data-cy="unsuccess-message">
                {message}
              </p>
            );
          } else {
            setBadErrorMessage(<ErrorMessageToUser />);
          }
          setShowCircularProgress(false);
        },
      });

      return () => {
        if (myProfileProxySetSubscription) {
          myProfileProxySetSubscription.unsubscribe();
        }
      };
    },
    [myProfileProxy]
  );

  const { setState, state, disable, handleValidationOnChange, handleOnSubmit } = useForm(
    initialState,
    validationSchema,
    saveHouseholdInfo
  );

  useEffect(() => {
    const myProfileObservable = myProfileProxy.get();
    const householdObservable = householdProxy.get();
    const householdMembersObservable = householdProxy.membersUnder15YearsOld();
    const combinedSubscriptions = combineLatest([myProfileObservable, householdObservable, householdMembersObservable]).subscribe(
      {
        next: (x) => {
          const allHouseholdMembers = x[1].householdMembers;
          const householdChildMembers = x[2];
          const householdChildMemberIdns = householdChildMembers.map((x) => x.idn);
          const householdMembersWithChildFlag = allHouseholdMembers.map((x) => ({
            ...x,
            childIndex: householdChildMemberIdns.indexOf(x.idn),
          }));
          setHouseholdMembers(householdMembersWithChildFlag);
          const children = householdMembersWithChildFlag.filter((member) => member.childIndex >= 0).sort(byAgeDisplay);
          setChildrenOfLoggedInUser(children);
          setDefaultHouseholdName(x[0]?.defaultHouseholdName ?? '');
          const newSelfDesignatedHouseholdName = x[0]?.selfDesignatedHouseholdName ?? '';
          setState({
            selfDesignatedHouseholdName: {
              value: newSelfDesignatedHouseholdName,
            },
          });
          setMessageToUser('');
          setShowCircularProgress(false);
        },
        error: () => {
          setBadErrorMessage(<ErrorMessageToUser />);
          setShowCircularProgress(false);
        },
      }
    );

    return () => {
      combinedSubscriptions.unsubscribe();
    };
  }, [myProfileProxy, householdProxy, setState]);

  // Clean up child registration message
  useEffect(
    () => () => {
      if (childRegistrationSuccessMessage) {
        setChildRegistrationSuccessMessage(null);
      }
    },
    [childRegistrationSuccessMessage, setChildRegistrationSuccessMessage]
  );

  return (
    <div>
      {badErrorMessage ?? (
        <div>
          {showCircularProgress ? (
            <CircularProgress data-cy="household-info-circular-progress" className="loading-animation" size={40} />
          ) : (
            <div>
              {childRegistrationSuccessMessage}
              {messageToUser}
            </div>
          )}
          <div>
            <div className="profile-box">
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small" aria-label="Table of household members">
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell>Members</StyledTableCell>
                      <StyledTableCell>Receives Household Mailings</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {householdMembers
                      ? householdMembers.map((member, index) => (
                          <StyledTableRow key={index} data-cy={`household-member-table-row-${index}`}>
                            <StyledTableCell component="th" scope="row">
                              <span>{member.personName}</span>
                            </StyledTableCell>
                            <StyledTableCell>{member.headOfHousehold ? 'Yes' : null}</StyledTableCell>
                          </StyledTableRow>
                        ))
                      : null}
                  </TableBody>
                </Table>
              </TableContainer>
              {childrenOfLoggedInUser?.length > 0 ? (
                <>
                  <br />
                  <TableContainer component={Paper} data-cy="household-children-container">
                    <Table className={classes.table} size="small" aria-label="Table of household children">
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCell>Bahá’í ID Number</StyledTableCell>
                          <StyledTableCell>Children</StyledTableCell>
                          <StyledTableCell>Age</StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {childrenOfLoggedInUser.map((child, index) => (
                          <StyledTableRow key={index} data-cy={`household-children-table-row-${index}`}>
                            <StyledTableCell component="th" scope="row">
                              <Link to={`/myprofile/child-information/${child.childIndex}`}>{child.idn}</Link>
                            </StyledTableCell>
                            <StyledTableCell>{`${child.firstName ?? ''} ${child.lastName}`}</StyledTableCell>
                            <StyledTableCell>{`${child.ageDisplay}`}</StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
              <div className="large-top-padding">
                <p>
                  Default Household Name:
                  <strong> {defaultHouseholdName}</strong>
                </p>
              </div>
              <Row>
                <Col sm={6} className="bottom-padding">
                  <em>
                    <p>Change Household Name:</p>
                    <p>
                      Enter and save a <strong>Self-Designated Household Name</strong> to override the auto-generated{' '}
                      <strong>Household Name</strong>.
                    </p>
                  </em>
                </Col>
                <Col sm={6}>
                  <form noValidate autoComplete="off">
                    <TextField
                      inputProps={{
                        maxLength: 50,
                        'data-cy': 'selfdesignated',
                      }}
                      id="selfdesignated"
                      name="selfDesignatedHouseholdName"
                      placeholder="Self-Designated Household Name"
                      label="Self-Designated Household Name"
                      aria-label="Self-Designated Household Name"
                      type="text"
                      fullWidth={true}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={state.selfDesignatedHouseholdName.value}
                      onChange={handleValidationOnChange}
                    />
                  </form>
                </Col>
              </Row>
              <MembershipNote />
            </div>
            <div className="medium-top-margin end">
              <input
                disabled={disable}
                onClick={handleOnSubmit}
                data-cy="HouseholdInfoSave"
                type="submit"
                value="Save"
                id="HouseholdInfoSave"
                className="primary-button"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withContext(HouseholdInfo);
